import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Flag, Upload, Map, Terminal, Shield, Key } from 'lucide-react';
import ToolCard from '../shared/ToolCard';
import { FeatureFlagsManager } from '../../Shared/FeatureFlags';
import FileUpload from '../../Shared/FileUpload';
import Sitemap from '../../Shared/Sitemap';
import SuperAdminPanel from '../../../pages/SuperAdminPanel';
import PermissionBrowser from '../tools/PermissionBrowser';
import CommandRunner from '../tools/CommandRunner';

const UtilityTools = () => {
  const [selectedTool, setSelectedTool] = useState(null);

  const utilityTools = [
    {
      id: 'featureFlags',
      icon: Flag,
      label: 'Feature Flags',
      description: 'Manage feature toggles and experimental features',
      action: 'Manage Flags'
    },
    {
      id: 'fileUpload',
      icon: Upload,
      label: 'File Upload Testing',
      description: 'Test file upload functionality and limits',
      action: 'Test Upload'
    },
    {
      id: 'sitemap',
      icon: Map,
      label: 'Application Sitemap',
      description: 'View application structure and navigation',
      action: 'View Sitemap'
    },
    {
      id: 'customCommands',
      icon: Terminal,
      label: 'Custom Commands',
      description: 'Execute custom development commands and utilities',
      action: 'Run Commands'
    },
    {
      id: 'userManager',
      icon: Shield,
      label: 'User Manager',
      description: 'Manage user assignments to sponsors, studies, and sites',
      action: 'Open Manager'
    },
    {
      id: 'permissionBrowser',
      icon: Key,
      label: 'Permission Browser',
      description: 'View and inspect user permissions, groups, and access rights',
      action: 'Browse Permissions'
    }
  ];

  const handleToolClick = (tool) => {
    setSelectedTool(selectedTool === tool.id ? null : tool.id);
  };

  const renderToolContent = (toolId) => {
    switch (toolId) {
      case 'featureFlags':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <FeatureFlagsManager />
          </div>
        );
        
      case 'fileUpload':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <FileUpload />
          </div>
        );
        
      case 'sitemap':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <Sitemap />
          </div>
        );
        
      case 'customCommands':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-1">Management Commands</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Run allowlisted Django management commands. Destructive commands require typing <code className="px-1 rounded bg-gray-100 dark:bg-gray-700">RUN</code> to confirm.
            </p>
            <CommandRunner />
          </div>
        );
        
      case 'userManager':
        return (
          <div className="mt-6">
            <SuperAdminPanel />
          </div>
        );
        
      case 'permissionBrowser':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <PermissionBrowser />
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {utilityTools.map((tool) => (
          <ToolCard
            key={tool.id}
            tool={tool}
            onClick={handleToolClick}
            className={selectedTool === tool.id ? 'ring-2 ring-primary-500' : ''}
          />
        ))}
      </div>

      {/* Render selected tool content */}
      {selectedTool && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          {renderToolContent(selectedTool)}
        </motion.div>
      )}
    </div>
  );
};

export default UtilityTools;
